import { cn } from '@rocco/utils/cn';
import { Button } from '@rocco/components/button';
import { ChevronRight, Check, X } from 'lucide-react';
import type { FollowSelectorItem as FollowSelectorItemType } from '../../types/follow-selector';

interface FollowSelectorItemProps {
    item: FollowSelectorItemType;
    onSelect: (item: FollowSelectorItemType) => void;
    isProcessing?: boolean;
    membershipLoading?: boolean;
}

/**
 * Individual list item in the FollowSelector.
 * Displays a single thumbnail, title, project name.
 * - For unsaved items: shows arrow by default, "Save" button on hover
 * - For saved items: always shows "Saved" button with tick, shows cross on hover to indicate removal
 */
export const FollowSelectorItem = ({
    item,
    onSelect,
    isProcessing = false,
    membershipLoading = false,
}: FollowSelectorItemProps) => {
    const handleClick = () => {
        if (!isProcessing) {
            onSelect(item);
        }
    };

    const thumbnail = item.thumbnail;
    const backgroundColor = thumbnail?.backgroundColor;
    const isMultiplied = !!thumbnail?.multiplied;
    const isClearCut = !!thumbnail?.isClearCut;
    const isProfessionalThumbnail = thumbnail?.type === 'Professional';
    /** Match TileMedia neutral background for multiplied assets. */
    const MULTIPLIED_THUMBNAIL_BG = '#f2f1f0';
    const usesNeutralThumbnailTreatment =
        isMultiplied ||
        isClearCut ||
        (isProfessionalThumbnail && !backgroundColor);
    const thumbnailContainerBackgroundColor =
        backgroundColor ||
        (usesNeutralThumbnailTreatment ? MULTIPLIED_THUMBNAIL_BG : undefined);
    const shouldContainThumbnail =
        !!thumbnailContainerBackgroundColor || usesNeutralThumbnailTreatment;

    return (
        <div className="px-4.5 md:pl-3.5">
            <button
                type="button"
                onClick={handleClick}
                disabled={isProcessing}
                className={cn(
                    'group relative box-content flex w-full items-center gap-3 rounded-lg border-[0.25rem] border-solid border-transparent text-left transition-colors',
                    'focus:bg-[#F1f1f1] focus:outline-none md:hover:border-[#f1f1f1] md:hover:bg-[#F1f1f1]',
                    isProcessing && 'cursor-wait opacity-70',
                )}
                aria-label={`Save to ${item.title}${item.projectName ? ` in ${item.projectName}` : ''}`}
            >
                {/* Single square thumbnail with pin-type-aware fit */}
                <div
                    className={cn(
                        'relative h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg',
                        !thumbnailContainerBackgroundColor && 'bg-gray-100',
                    )}
                    style={{
                        backgroundColor: thumbnailContainerBackgroundColor,
                    }}
                >
                    {thumbnail?.src ? (
                        <img
                            src={thumbnail.src}
                            alt={`Preview of ${item.title}`}
                            className={cn(
                                'h-full w-full',
                                shouldContainThumbnail
                                    ? 'object-contain p-1'
                                    : 'object-cover',
                            )}
                            style={{
                                mixBlendMode: usesNeutralThumbnailTreatment
                                    ? 'multiply'
                                    : undefined,
                            }}
                        />
                    ) : (
                        <div
                            className="flex h-full w-full items-center justify-center bg-gray-200"
                            aria-label="No preview available"
                        />
                    )}
                </div>

                {/* Title and project name */}
                <div
                    className={cn(
                        'min-w-0 flex-1',
                        item.isSaved && !membershipLoading && 'pr-[7rem]',
                    )}
                >
                    <p className="text-sf-pro truncate text-base font-medium -tracking-[0.009375rem] text-gray-900">
                        {item.title}
                    </p>
                    {/* Hide project name for personal moodboards */}
                    {item.projectName && !item.isPersonal && (
                        <p className="text-sf-pro truncate text-3 leading-5 text-gray-900">
                            {item.projectName}
                        </p>
                    )}
                </div>

                {/* For unsaved items: Arrow indicator - always visible on mobile, hidden on hover on desktop */}
                {!item.isSaved && (
                    <div className="flex-shrink-0 opacity-100 transition-opacity duration-200 md:group-hover:opacity-0">
                        <ChevronRight className="h-5 w-5 text-gray-900" />
                    </div>
                )}

                {/* For unsaved items: Save button - hidden by default, shows on hover on desktop only */}
                {!item.isSaved && (
                    <div className="absolute right-1 flex-shrink-0 opacity-0 transition-opacity duration-200 md:group-hover:opacity-100">
                        <Button
                            type="button"
                            size="narrow"
                            color="dark"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleClick();
                            }}
                            disabled={isProcessing}
                            className="min-w-[4.5rem] font-medium"
                        >
                            Save
                        </Button>
                    </div>
                )}

                {/* For saved items: Saved button with tick - always visible, shows cross on hover (desktop only) */}
                {/* Hide button while membership is loading to prevent showing stale data */}
                {item.isSaved && !membershipLoading && (
                    <div className="absolute right-1 flex-shrink-0">
                        <Button
                            type="button"
                            size="narrow"
                            color="dark"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleClick();
                            }}
                            disabled={isProcessing}
                            className="relative min-w-[4.5rem] gap-1.5 font-medium"
                        >
                            <span className="relative inline-flex h-4 w-4 items-center justify-center">
                                <Check className="absolute h-4 w-4 transition-opacity duration-200 md:group-hover:opacity-0" />
                                <X className="absolute h-4 w-4 opacity-0 transition-opacity duration-200 md:group-hover:opacity-100" />
                            </span>
                            Saved
                        </Button>
                    </div>
                )}
            </button>
        </div>
    );
};
